import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useAuthStore } from "@/shared/state/authstore";
import {
  fetchCurrentAccount,
  updateAccount,
  fetchEmployeeSkillTypes,
  fetchEmployeeCredentialTypes,
  createEmployeeSkillType,
  updateEmployeeSkillType,
  deleteEmployeeSkillType,
  createEmployeeCredentialType,
  updateEmployeeCredentialType,
  deleteEmployeeCredentialType,
} from "../api/accountApi";
import type { EmployeeSkillTypeRequest, EmployeeCredentialTypeRequest } from "./types";

export const accountKeys = {
  all: ["account"] as const,
  details: () => [...accountKeys.all, "details"] as const,
  employeeSkills: () => [...accountKeys.all, "employee-skills"] as const,
  employeeCredentials: () => [...accountKeys.all, "employee-credentials"] as const,
};

export function useCurrentAccount(options?: { enabled?: boolean }) {
  const accountId = useAuthStore((s) => s.accountId);
  return useQuery({
    queryKey: accountKeys.details(),
    queryFn: fetchCurrentAccount,
    staleTime: 4 * 60 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
    enabled: (options?.enabled ?? true) && accountId !== null,
  });
}

export const useUpdateAccount = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateAccount,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: accountKeys.details() });
    }
  });
}

// ============================================
// Employee Skill Types
// ============================================

export const useEmployeeSkillTypes = () => {
  return useQuery({
    queryKey: accountKeys.employeeSkills(),
    queryFn: fetchEmployeeSkillTypes,
    staleTime: 60 * 60 * 1000,
  });
};

export const useCreateEmployeeSkillType = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: EmployeeSkillTypeRequest) => createEmployeeSkillType(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: accountKeys.employeeSkills() });
      toast.success("Skill type created");
    },
  });
};

export const useUpdateEmployeeSkillType = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: EmployeeSkillTypeRequest }) =>
      updateEmployeeSkillType(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: accountKeys.employeeSkills() });
      toast.success("Skill type updated");
    },
  });
};

export const useDeleteEmployeeSkillType = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => deleteEmployeeSkillType(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: accountKeys.employeeSkills() });
      toast.success("Skill type deleted");
    },
  });
};

// ============================================
// Employee Credential Types
// ============================================

export const useEmployeeCredentialTypes = () => {
  return useQuery({
    queryKey: accountKeys.employeeCredentials(),
    queryFn: fetchEmployeeCredentialTypes,
    staleTime: 60 * 60 * 1000,
  });
};

export const useCreateEmployeeCredentialType = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: EmployeeCredentialTypeRequest) => createEmployeeCredentialType(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: accountKeys.employeeCredentials() });
      toast.success("Credential type created");
    },
  });
};

export const useUpdateEmployeeCredentialType = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: EmployeeCredentialTypeRequest }) =>
      updateEmployeeCredentialType(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: accountKeys.employeeCredentials() });
      toast.success("Credential type updated");
    },
  });
};

export const useDeleteEmployeeCredentialType = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => deleteEmployeeCredentialType(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: accountKeys.employeeCredentials() });
      toast.success("Credential type deleted");
    },
  });
};
